import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumBmEntityType } from "./enums/enumBmEntityType.generated";
import { EnumTaskRepeatBasis } from "./enums/enumTaskRepeatBasis.generated";
import { EnumTaskStatus } from "./enums/enumTaskStatus.generated";

// Model Objects

export class BmTaskReadOnly extends ModelObject<BmTaskReadOnly> {

    private _cancelledAt: Instant | null = null;
    /**
    * Timestamp at which the task was cancelled on the system. Captured by system not user editable.
    */
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    private _cancelledByUserId: Guid | null = null;
    /**
    * User Id of the user who cancelled the task (if cancelled). Captured by system not user editable.
    */
    public get cancelledByUserId(): Guid | null { return this._cancelledByUserId }
    public set cancelledByUserId(value: Guid | null) {
      this._cancelledByUserId = value === undefined ? null : value;
    }
    private _completedAt: Instant | null = null;
    /**
    * Timestamp at which the task was completed on the system. Captured by system not user editable.
    */
    public get completedAt(): Instant | null { return this._completedAt }
    public set completedAt(value: Instant | null) {
      this._completedAt = value === undefined ? null : value;
    }
    private _completedByUserId: Guid | null = null;
    /**
    * User Id of the user who completed the task (if complete). Captured by system not user editable.
    */
    public get completedByUserId(): Guid | null { return this._completedByUserId }
    public set completedByUserId(value: Guid | null) {
      this._completedByUserId = value === undefined ? null : value;
    }
    /**
    * Timestamp at which the task was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * User Id of the creating user. Captured by system not user editable.
    */
    public createdByUserId: Guid = Guid.createEmpty();
    private _notifiedDueSoonAt: Instant | null = null;
    /**
    * Timestamp at which DueSoon notifications were last sent. Captured by system not user editable.
    */
    public get notifiedDueSoonAt(): Instant | null { return this._notifiedDueSoonAt }
    public set notifiedDueSoonAt(value: Instant | null) {
      this._notifiedDueSoonAt = value === undefined ? null : value;
    }
    private _notifiedOverdueAt: Instant | null = null;
    /**
    * Timestamp at which OverDue notifications were last sent. Captured by system not user editable.
    */
    public get notifiedOverdueAt(): Instant | null { return this._notifiedOverdueAt }
    public set notifiedOverdueAt(value: Instant | null) {
      this._notifiedOverdueAt = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.cancelledByUserId = jsonSerializedObject.cancelledByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.cancelledByUserId);
          this.completedAt = jsonSerializedObject.completedAt == null ? null : Instant.parse(jsonSerializedObject.completedAt);
          this.completedByUserId = jsonSerializedObject.completedByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.completedByUserId);
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.createdByUserId = Guid.parseOrEmpty(jsonSerializedObject.createdByUserId);
          this.notifiedDueSoonAt = jsonSerializedObject.notifiedDueSoonAt == null ? null : Instant.parse(jsonSerializedObject.notifiedDueSoonAt);
          this.notifiedOverdueAt = jsonSerializedObject.notifiedOverdueAt == null ? null : Instant.parse(jsonSerializedObject.notifiedOverdueAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.cancelledByUserId = this.cancelledByUserId == null ? null : this.cancelledByUserId.toString();
        toRet.completedAt = this.completedAt == null ? null : this.completedAt.toString();
        toRet.completedByUserId = this.completedByUserId == null ? null : this.completedByUserId.toString();
        toRet.createdAt = this.createdAt.toString();
        toRet.createdByUserId = this.createdByUserId.toString();
        toRet.notifiedDueSoonAt = this.notifiedDueSoonAt == null ? null : this.notifiedDueSoonAt.toString();
        toRet.notifiedOverdueAt = this.notifiedOverdueAt == null ? null : this.notifiedOverdueAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "cancelledByUserId", rule: (v: any) => v == null || !v.isEmpty() || "BmTaskReadOnly.CancelledByUserId cannot be empty"},
            {field: "completedByUserId", rule: (v: any) => v == null || !v.isEmpty() || "BmTaskReadOnly.CompletedByUserId cannot be empty"},
            {field: "createdByUserId", rule: (v: any) => !v.isEmpty() || "BmTaskReadOnly.CreatedByUserId cannot be empty"},
    ];
}


export class BmTaskCalculated extends ModelObject<BmTaskCalculated> {

    public status: EnumTaskStatus = EnumTaskStatus.open;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.status = EnumTaskStatus.getByValue(jsonSerializedObject.status, this.status);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.status = this.status.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class BmTask extends ModelObject<BmTask> {

    public id: Guid = Guid.createEmpty();
    public version: number = 0;
    private _assignedToUserId: Guid | null = null;
    /**
    * User, if any, currently assigned to perform the task.
    */
    public get assignedToUserId(): Guid | null { return this._assignedToUserId }
    public set assignedToUserId(value: Guid | null) {
      this._assignedToUserId = value === undefined ? null : value;
    }
    private _associatedBmEntityId: Guid | null = null;
    /**
    * Id of the entity with which the task is associated, if any.
    */
    public get associatedBmEntityId(): Guid | null { return this._associatedBmEntityId }
    public set associatedBmEntityId(value: Guid | null) {
      this._associatedBmEntityId = value === undefined ? null : value;
    }
    /**
    * Task Description
    * Not Nullable
    * Max Length : 100000
    */
    public description: string = "";
    private _dueDate: LocalDate | null = null;
    /**
    * Date by which the task should be done, if specified.
    */
    public get dueDate(): LocalDate | null { return this._dueDate }
    public set dueDate(value: LocalDate | null) {
      this._dueDate = value === undefined ? null : value;
    }
    private _dueTime: LocalTime | null = null;
    /**
    * Time on due date by which the task should be done, if specified. Not relevant if Due Date is null.
    */
    public get dueTime(): LocalTime | null { return this._dueTime }
    public set dueTime(value: LocalTime | null) {
      this._dueTime = value === undefined ? null : value;
    }
    public taskQueueId: Guid = Guid.createEmpty();
    /**
    * Basis for repeating task once completed. Should be None if DueDate is not null.
    */
    public taskRepeatBasis: EnumTaskRepeatBasis = EnumTaskRepeatBasis.none;
    /**
    * Parameter for task repeat schedule. Only relevant if TaskRepeatBasis is a basis which uses a parameter.
    */
    public taskRepeatParameter: number = 0;
    public taskTypeId: Guid = Guid.createEmpty();
    public readOnly: BmTaskReadOnly = new BmTaskReadOnly();
    public calculated: BmTaskCalculated = new BmTaskCalculated();
    /**
    * Type of the entity with which the task is associated, or enum value none. Association is only supported to entity types which are tenant-level with a single guid id.
    */
    public associatedBmEntityType: EnumBmEntityType = EnumBmEntityType.none;
    /**
    * Task Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Duration of this task in minutes.
    * Minimum : 1
    */
    public durationMinutes: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.version = jsonSerializedObject.version;
          this.assignedToUserId = jsonSerializedObject.assignedToUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.assignedToUserId);
          this.associatedBmEntityId = jsonSerializedObject.associatedBmEntityId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.associatedBmEntityId);
          this.description = jsonSerializedObject.description;
          this.dueDate = jsonSerializedObject.dueDate == null ? null : LocalDate.parse(jsonSerializedObject.dueDate);
          this.dueTime = jsonSerializedObject.dueTime == null ? null : LocalTime.parse(jsonSerializedObject.dueTime);
          this.taskQueueId = Guid.parseOrEmpty(jsonSerializedObject.taskQueueId);
          this.taskRepeatBasis = EnumTaskRepeatBasis.getByValue(jsonSerializedObject.taskRepeatBasis, this.taskRepeatBasis);
          this.taskRepeatParameter = jsonSerializedObject.taskRepeatParameter;
          this.taskTypeId = Guid.parseOrEmpty(jsonSerializedObject.taskTypeId);
          this.readOnly = new BmTaskReadOnly(jsonSerializedObject.readOnly);
          this.calculated = new BmTaskCalculated(jsonSerializedObject.calculated);
          this.associatedBmEntityType = EnumBmEntityType.getByValue(jsonSerializedObject.associatedBmEntityType, this.associatedBmEntityType);
          this.notes = jsonSerializedObject.notes;
          this.durationMinutes = jsonSerializedObject.durationMinutes;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.version = this.version;
        toRet.assignedToUserId = this.assignedToUserId == null ? null : this.assignedToUserId.toString();
        toRet.associatedBmEntityId = this.associatedBmEntityId == null ? null : this.associatedBmEntityId.toString();
        toRet.description = this.description;
        toRet.dueDate = this.dueDate == null ? null : this.dueDate.toString();
        toRet.dueTime = this.dueTime == null ? null : this.dueTime.toString();
        toRet.taskQueueId = this.taskQueueId.toString();
        toRet.taskRepeatBasis = this.taskRepeatBasis.value;
        toRet.taskRepeatParameter = this.taskRepeatParameter;
        toRet.taskTypeId = this.taskTypeId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.calculated = this.calculated.toJsonSerializedObject();
        toRet.associatedBmEntityType = this.associatedBmEntityType.value;
        toRet.notes = this.notes;
        toRet.durationMinutes = this.durationMinutes;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "BmTask.Id cannot be empty"},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "assignedToUserId", rule: (v: any) => v == null || !v.isEmpty() || "BmTask.AssignedToUserId cannot be empty"},
            {field: "associatedBmEntityId", rule: (v: any) => v == null || !v.isEmpty() || "BmTask.AssociatedBmEntityId cannot be empty"},
            {field: "description", rule: (v: any) => v == null || v.length <= 100000 || "Description has too many characters (100000)"},
            {field: "taskQueueId", rule: (v: any) => !v.isEmpty() || "BmTask.TaskQueueId cannot be empty"},
            {field: "taskRepeatParameter", rule: (v: any) => v == null || v >= -2147483648 || "TaskRepeatParameter is not greater than minimum allowed value: -2147483648"},
            {field: "taskRepeatParameter", rule: (v: any) => v == null || v <= 2147483647 || "TaskRepeatParameter is above maximum allowed value: 2147483647"},
            {field: "taskTypeId", rule: (v: any) => !v.isEmpty() || "BmTask.TaskTypeId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "durationMinutes", rule: (v: any) => v == null || v >= 1 || "DurationMinutes is not greater than minimum allowed value: 1"},
            {field: "durationMinutes", rule: (v: any) => v == null || v <= 2147483647 || "DurationMinutes is above maximum allowed value: 2147483647"},
    ];
}


export class BmTaskFilterAssociatedEntity extends ModelObject<BmTaskFilterAssociatedEntity> {

    public entityType: EnumBmEntityType = EnumBmEntityType.none;
    public entityId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.entityType = EnumBmEntityType.getByValue(jsonSerializedObject.entityType, this.entityType);
          this.entityId = Guid.parseOrEmpty(jsonSerializedObject.entityId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.entityType = this.entityType.value;
        toRet.entityId = this.entityId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "entityId", rule: (v: any) => !v.isEmpty() || "BmTaskFilterAssociatedEntity.EntityId cannot be empty"},
    ];
}


export class BmTaskFilter extends ModelObject<BmTaskFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _limitStatuses: EnumTaskStatus[] | null = null;
    public get limitStatuses(): EnumTaskStatus[] | null { return this._limitStatuses }
    public set limitStatuses(value: EnumTaskStatus[] | null) {
      this._limitStatuses = value === undefined ? null : value;
    }
    private _limitTaskTypeIds: Guid[] | null = null;
    public get limitTaskTypeIds(): Guid[] | null { return this._limitTaskTypeIds }
    public set limitTaskTypeIds(value: Guid[] | null) {
      this._limitTaskTypeIds = value === undefined ? null : value;
    }
    private _limitTaskQueueIds: Guid[] | null = null;
    public get limitTaskQueueIds(): Guid[] | null { return this._limitTaskQueueIds }
    public set limitTaskQueueIds(value: Guid[] | null) {
      this._limitTaskQueueIds = value === undefined ? null : value;
    }
    private _limitAssignedUserIds: Guid[] | null = null;
    public get limitAssignedUserIds(): Guid[] | null { return this._limitAssignedUserIds }
    public set limitAssignedUserIds(value: Guid[] | null) {
      this._limitAssignedUserIds = value === undefined ? null : value;
    }
    private _fromDueDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) due date. Note tasks with due date null will be allowed by this filter.
    */
    public get fromDueDate(): LocalDate | null { return this._fromDueDate }
    public set fromDueDate(value: LocalDate | null) {
      this._fromDueDate = value === undefined ? null : value;
    }
    private _toDueDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) due date. Note tasks with due date null will be rejected by this filter.
    */
    public get toDueDate(): LocalDate | null { return this._toDueDate }
    public set toDueDate(value: LocalDate | null) {
      this._toDueDate = value === undefined ? null : value;
    }
    private _limitAssociatedEntities: BmTaskFilterAssociatedEntity[] | null = null;
    public get limitAssociatedEntities(): BmTaskFilterAssociatedEntity[] | null { return this._limitAssociatedEntities }
    public set limitAssociatedEntities(value: BmTaskFilterAssociatedEntity[] | null) {
      this._limitAssociatedEntities = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.limitStatuses = jsonSerializedObject.limitStatuses == null ? null : jsonSerializedObject.limitStatuses.map((v: any) => EnumTaskStatus.getByValue(v));
          this.limitTaskTypeIds = jsonSerializedObject.limitTaskTypeIds == null ? null : jsonSerializedObject.limitTaskTypeIds.map((v: any) => Guid.parseOrEmpty(v));
          this.limitTaskQueueIds = jsonSerializedObject.limitTaskQueueIds == null ? null : jsonSerializedObject.limitTaskQueueIds.map((v: any) => Guid.parseOrEmpty(v));
          this.limitAssignedUserIds = jsonSerializedObject.limitAssignedUserIds == null ? null : jsonSerializedObject.limitAssignedUserIds.map((v: any) => Guid.parseOrEmpty(v));
          this.fromDueDate = jsonSerializedObject.fromDueDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDueDate);
          this.toDueDate = jsonSerializedObject.toDueDate == null ? null : LocalDate.parse(jsonSerializedObject.toDueDate);
          this.limitAssociatedEntities = jsonSerializedObject.limitAssociatedEntities == null ? null : jsonSerializedObject.limitAssociatedEntities.map((v: any) => new BmTaskFilterAssociatedEntity(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.limitStatuses = this.limitStatuses == null ? null : this.limitStatuses.map(v => v.value);
        toRet.limitTaskTypeIds = this.limitTaskTypeIds == null ? null : this.limitTaskTypeIds.map(v => v.toString());
        toRet.limitTaskQueueIds = this.limitTaskQueueIds == null ? null : this.limitTaskQueueIds.map(v => v.toString());
        toRet.limitAssignedUserIds = this.limitAssignedUserIds == null ? null : this.limitAssignedUserIds.map(v => v.toString());
        toRet.fromDueDate = this.fromDueDate == null ? null : this.fromDueDate.toString();
        toRet.toDueDate = this.toDueDate == null ? null : this.toDueDate.toString();
        toRet.limitAssociatedEntities = this.limitAssociatedEntities == null ? null : this.limitAssociatedEntities.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
            {field: "limitAssociatedEntities", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as BmTaskFilterAssociatedEntity[]).reduce((acc: Array<string>, curr: BmTaskFilterAssociatedEntity) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class FilteredBmTaskResults extends ModelObject<FilteredBmTaskResults> {

    public matchingCount: number = 0;
    public bmTasks: BmTask[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.bmTasks = jsonSerializedObject.bmTasks.map((v: any) => new BmTask(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.bmTasks = this.bmTasks.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "bmTasks", rule: (v: any) => !!v || "bmTasks is required"},
            {field: "bmTasks", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as BmTask[]).reduce((acc: Array<string>, curr: BmTask) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class BmTaskService extends ServiceBase {

  public async getBmTask(tenantId: Guid, bmTaskId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BmTask> {
    const req = {
    tenantId:tenantId.toString(),
    bmTaskId:bmTaskId.toString(),
    };
    const extractor = (response:any) => new BmTask(response.bmTask);;
    return this.makeJsonRequest("bmTask/v1/getBmTask", req, false, extractor, opts);
  };

  public async getBmTasks(tenantId: Guid, bmTaskIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BmTask[]> {
    const req = {
    tenantId:tenantId.toString(),
    bmTaskIds:bmTaskIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.bmTasks.map((v: any) => new BmTask(v));;
    return this.makeJsonRequest("bmTask/v1/getBmTasks", req, false, extractor, opts);
  };

  public async getBmTasksByFilter(tenantId: Guid, filter: BmTaskFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredBmTaskResults> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredBmTaskResults(response.results);;
    return this.makeJsonRequest("bmTask/v1/getBmTasksByFilter", req, false, extractor, opts);
  };

  public async saveBmTask(tenantId: Guid, bmTask: BmTask, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    bmTask:bmTask.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("bmTask/v1/saveBmTask", req, true, extractor, opts);
  };

  public async saveBmTasks(tenantId: Guid, bmTasks: readonly BmTask[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    bmTasks:bmTasks.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("bmTask/v1/saveBmTasks", req, true, extractor, opts);
  };

  public async cancelBmTask(tenantId: Guid, bmTaskId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    bmTaskId:bmTaskId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("bmTask/v1/cancelBmTask", req, true, extractor, opts);
  };

  public async completeBmTask(tenantId: Guid, bmTaskId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    bmTaskId:bmTaskId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("bmTask/v1/completeBmTask", req, true, extractor, opts);
  };

  public async completeBmTasks(tenantId: Guid, bmTaskIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    bmTaskIds:bmTaskIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("bmTask/v1/completeBmTasks", req, true, extractor, opts);
  };
}

export const bmTaskService = new BmTaskService();